import { useMemo } from "react";
import type { ClassData } from "../../../interfaces/classSessions";
import { useLocalization } from "../../../stores/localizationContext";
import { formatDuration } from "../../../utils";
import "./Card.scss";

interface CardProps {
  type?: "session" | "class";
  title: string;
  subject: string;
  level?: string;
  term?: string;
  curriculum?: string;
  schedule?: string;
  date?: string;
  time?: string;
  duration?: string | number;
  nagwaStudents?: number;
  affiliateStudents?: number;
  status?: string;
  statusId?: number;
  classId?: number;
  classes?: ClassData[];
  canStart?: boolean;
  onPreview?: () => void;
  onStart?: () => void;
  /** Shown instead of Preview/Start when the session has ended */
  onViewSession?: () => void;
}

const Card: React.FC<CardProps> = ({
  type,
  title,
  subject,
  level,
  term,
  curriculum,
  schedule,
  date,
  time,
  duration,
  nagwaStudents,
  affiliateStudents,
  status,
  statusId,
  classId,
  classes,
  canStart,
  onPreview,
  onStart,
  onViewSession,
}) => {
  const { t } = useLocalization();

  const isEndedSession =
    status?.toLowerCase() === "ended" || statusId === 3;

  const classData =
    classId && classes ? classes.find((cls) => cls.class_id === classId) : null;

  // Use localized grade from class data or fallback to provided level
  const localizedLevel = classData?.class_subjects?.[0]?.grade_localization_key
    ? t(
        classData.class_subjects[0].grade_localization_key,
        level || "Secondary School"
      )
    : level || "Secondary School";

  // Use curriculum from class data or fallback to provided curriculum
  const localizedCurriculum = classData
    ? classData.class_type
    : curriculum || "Regular";

  // Use term from class data or fallback
  const localizedTerm = classData?.class_semester_localization_key
    ? t(classData.class_semester_localization_key, term || "First Term")
    : term || "";

  // Use subject from class data or fallback to provided subject
  const localizedSubject = classData?.class_subjects?.[0]?.subject_name || subject || "";

  // Format duration with localized text
  const formattedDuration = useMemo(() => {
    return formatDuration(duration, t);
  }, [duration, t]);

  return (
    <div className={`card ${type}-card`}>
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
        <p className="card-level">{localizedLevel || level}</p>
      </div>

      <div className="card-content">
        {(type === "session" || type === "class") && (
          <>
            {type === "session" && (
              <>
                <p className="card-subject">
                  {subject}
                  {localizedTerm ? ` • ${localizedTerm}` : ""}
                  {localizedCurriculum ? ` • ${localizedCurriculum}` : ""}
                </p>

                <p className="card-datetime">
                  {time ? `${date} • ${time}` : date}
                </p>
              </>
            )}

            {type === "class" && (
              <>
                <p className="card-subject">
                  {localizedSubject}
                  {localizedTerm ? ` • ${localizedTerm}` : ""}
                  {localizedCurriculum ? ` • ${localizedCurriculum}` : ""}
                </p>
                <div className="card-schedule">
                  <p className="schedule-time">{schedule}</p>
                </div>
                <div className="card-students">
                  <p className="students-label">
                    {t("students_number", "Students Number")}:
                  </p>
                  <p className="students-count">
                    {t("nagwa_students", "Nagwa Students")}: {nagwaStudents} •{" "}
                    {t("affiliate_students_label")}:{" "}
                    {affiliateStudents}
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {type === "session" && (
        <div className="card-footer">
          <div className="duration">
            <i className="f-icon fa-light stopwatch-icon"></i>
            <span>{formattedDuration}</span>
          </div>
          <div className="card-actions">
            {isEndedSession && onViewSession ? (
              <button className="btn btn-primary" onClick={onViewSession}>
                {t("view_session", "View session")}
              </button>
            ) : (
              <>
                <button className="btn btn-outline-black" onClick={onPreview}>
                  {t("preview_label")}
                </button>
                <button className="btn btn-primary" onClick={onStart} disabled={!canStart}>
                  {t(
                    status === "In Progress" ? "join" : "start",
                    status === "In Progress" ? "Join" : "Start"
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;
